/**
 * BinomDrawerContext — owns the unified Binom draft (flow paths + campaign
 * metadata) so peer tabs (Standard / Campaign / Advanced) share one source of
 * truth. The provider also registers footer save actions so the three save
 * buttons work no matter which tab is active.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  createContext,
  useContext,
  useEffect,
  type FC,
  type ReactNode,
} from 'react';

import { useBinomCampaignRaw, useBinomFlow } from '@/hooks/binom';

import { useFlowOptimizerDrawerFooter } from '@/features/flow-optimizer/FlowOptimizerDrawerFooterContext';

import type {
  BinomCampaignMetaDraft,
  BinomDraftItem,
  BinomDraftPath,
  BinomFlowDraft,
} from './binom-flow-draft.types';
import { useBinomFlowDraft } from './use-binom-flow-draft';
import { useBinomDrawerSave } from './use-binom-drawer-save';
import type { PathLocator } from './draft-structural';

interface BinomDrawerCtxValue {
  flowId: string;
  campaignId?: string;
  draft: BinomFlowDraft | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  /** Whether the raw campaign detail (Main Options / Advanced fields) is still loading. */
  campaignLoading: boolean;
  patchDefaultPath: (idx: number, m: (p: BinomDraftPath) => void) => void;
  patchGroupPath: (
    groupIdx: number,
    pathIdx: number,
    m: (p: BinomDraftPath) => void,
  ) => void;
  patchCampaignMeta: (m: (d: BinomCampaignMetaDraft) => void) => void;
  addDefaultPath: () => void;
  removeDefaultPath: (idx: number) => void;
  moveDefaultPath: (from: number, to: number) => void;
  addGroupPath: (groupIdx: number) => void;
  removeGroupPath: (groupIdx: number, pathIdx: number) => void;
  moveGroupPath: (groupIdx: number, from: number, to: number) => void;
  addLanderTo: (loc: PathLocator, item: BinomDraftItem) => void;
  removeLanderFrom: (loc: PathLocator, idx: number) => void;
  moveLanderIn: (loc: PathLocator, from: number, to: number) => void;
  addOfferTo: (loc: PathLocator, item: BinomDraftItem) => void;
  removeOfferFrom: (loc: PathLocator, idx: number) => void;
  moveOfferIn: (loc: PathLocator, from: number, to: number) => void;
  reset: () => void;
  dirty: boolean;
}

const BinomDrawerContext = createContext<BinomDrawerCtxValue | null>(null);

export function useBinomDrawer(): BinomDrawerCtxValue {
  const ctx = useContext(BinomDrawerContext);
  if (!ctx) throw new Error('BinomDrawerProvider is required');
  return ctx;
}

export interface BinomDrawerProviderProps {
  flowId: string;
  campaignId?: string;
  killSwitch: boolean;
  drawerActive: boolean;
  children: ReactNode;
}

export const BinomDrawerProvider: FC<BinomDrawerProviderProps> = ({
  flowId,
  campaignId,
  killSwitch,
  drawerActive,
  children,
}) => {
  const { setSlot, setHeaderSlot } = useFlowOptimizerDrawerFooter();
  const flowQuery = useBinomFlow(flowId);
  const campaignQuery = useBinomCampaignRaw(campaignId);
  const draftState = useBinomFlowDraft(
    flowQuery.data ?? undefined,
    campaignQuery.data ?? null,
  );
  const { applying, actions } = useBinomDrawerSave({
    flowId,
    campaignId,
    draft: draftState.draft,
    dirty: draftState.dirty,
    killSwitch,
    flowQuery,
    campaignQuery,
  });

  useEffect(() => {
    const ready = !flowQuery.isLoading && !flowQuery.isError && draftState.draft;
    if (!ready || !drawerActive) {
      setSlot(null);
      setHeaderSlot(null);
      return;
    }
    setSlot({
      dirty: draftState.dirty,
      applying,
      onDiscard: draftState.reset,
      actions,
      applyNotice: null,
    });
    setHeaderSlot({
      flowName: draftState.draft?.name ?? '',
      dirty: draftState.dirty,
      syncing: flowQuery.isFetching,
      onRefresh: () => void flowQuery.refetch(),
    });
    return () => {
      setSlot(null);
      setHeaderSlot(null);
    };
  }, [
    drawerActive,
    flowQuery.isLoading,
    flowQuery.isError,
    flowQuery.isFetching,
    draftState.draft,
    draftState.dirty,
    draftState.reset,
    applying,
    actions,
    setSlot,
    setHeaderSlot,
    flowQuery,
  ]);

  const value: BinomDrawerCtxValue = {
    flowId,
    campaignId,
    draft: draftState.draft,
    isLoading: flowQuery.isLoading,
    isError: flowQuery.isError,
    errorMessage:
      flowQuery.error instanceof Error ? flowQuery.error.message : undefined,
    campaignLoading: Boolean(campaignId) && campaignQuery.isLoading,
    patchDefaultPath: draftState.patchDefaultPath,
    patchGroupPath: draftState.patchGroupPath,
    patchCampaignMeta: draftState.patchCampaignMeta,
    addDefaultPath: draftState.addDefaultPath,
    removeDefaultPath: draftState.removeDefaultPath,
    moveDefaultPath: draftState.moveDefaultPath,
    addGroupPath: draftState.addGroupPath,
    removeGroupPath: draftState.removeGroupPath,
    moveGroupPath: draftState.moveGroupPath,
    addLanderTo: draftState.addLanderTo,
    removeLanderFrom: draftState.removeLanderFrom,
    moveLanderIn: draftState.moveLanderIn,
    addOfferTo: draftState.addOfferTo,
    removeOfferFrom: draftState.removeOfferFrom,
    moveOfferIn: draftState.moveOfferIn,
    reset: draftState.reset,
    dirty: draftState.dirty,
  };

  return (
    <BinomDrawerContext.Provider value={value}>
      {children}
    </BinomDrawerContext.Provider>
  );
};
